"use client";

import QtyButtons from "@/components/qty-buttons";
import RemoveProduct from "@/components/remove-product";
import { buttonVariants } from "@/components/ui/button";

import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { cn, formatDateTime } from "@/lib/utils";
import { useCartStore } from "@/providers/cart-store-provider";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { motion } from "framer-motion";
import { ShoppingCartIcon } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";

export default function MiniCart() {
  const items = useCartStore((state) => state.items);

  const totalQuantity = useCartStore((state) => state.totalQuantity());
  const totalPrice = useCartStore((state) => state.totalPrice());

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null; // Avoid rendering during SSR
  }

  return (
    <Sheet>
      <motion.div whileHover={{ rotateZ: -4 }}>
        <SheetTrigger aria-label="öppna varukorgen" asChild>
          <button className="relative p-1 rounded-sm">
            {totalQuantity > 0 && (
              <span className=" absolute top-0 -right-2 bg-red-600 rounded-full size-[19px] text-white text-[12px] font-mono flex items-center justify-center">
                {totalQuantity}
              </span>
            )}
            <span className="sr-only">öppna varukorgen</span>
            <ShoppingCartIcon className="text-primary-foreground" />
          </button>
        </SheetTrigger>
      </motion.div>
      <SheetContent className="!min-w-[95%] md:!min-w-[85%] lg:!min-w-[652px] ">
        <SheetHeader>
          <SheetTitle>Varukorg</SheetTitle>
        </SheetHeader>
        <div className="flex flex-col justify-between h-full py-10">
          <ScrollArea className="h-[60vh] my-4 px-4 rounded-md ">
            <ul className="p-4">
              {items.length > 0 &&
                items.map((item) => {
                  const formatDate = item?.date
                    ? formatDateTime(item?.date)
                    : undefined;
                  return (
                    <li
                      key={item?.databaseId}
                      className="flex items-left justify-start gap-2 "
                    >
                      <div className="py-5">
                        <RemoveProduct product={item} />
                      </div>
                      <div className="border-b w-full py-4 flex justify-between ">
                        <div>
                          <p className="text-base pt-[5px] pb-1">
                            {item?.name}
                          </p>
                          {formatDate && (
                            <time className="text-sm text-muted-foreground">
                              {formatDate.day} {"/"} {formatDate.month} -{" "}
                              {formatDate.time}
                            </time>
                          )}
                        </div>

                        <div className="flex justify-start items-start gap-2 ">
                          <QtyButtons
                            id={item.databaseId}
                            quantity={item.quantity}
                            available_quantity={item.available_quantity}
                          />
                          <div className="w-[75px] flex justify-end py-[5px]">
                            {item.quantity > 1 ? (
                              <span>{item.price * item.quantity} SEK</span>
                            ) : (
                              <span>{item.price} SEK</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </ScrollArea>
          <div className=" flex flex-col gap-6 border-t-2 pt-6 border-primary-red">
            <div className="flex justify-between items-center">
              <p className="text-2xl font-bold">Total</p>
              <p className="text-2xl font-bold">{totalPrice} SEK</p>
            </div>
            <div className="flex justify-between items-center">
              <SheetClose asChild>
                <Link
                  href="/vara-provningar"
                  className={cn(
                    buttonVariants({ variant: "default", size: "lg" }),
                    "uppercase"
                  )}
                >
                  Fortsätt handla
                </Link>
              </SheetClose>
              <SheetClose asChild>
                <Link
                  href="/kassan"
                  className={cn(
                    buttonVariants({ variant: "outline", size: "lg" }),
                    "uppercase border-2"
                  )}
                >
                  Gå till kassan
                </Link>
              </SheetClose>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
